import { useRef } from 'react';
import { useMotion } from '@/hooks/useEditorialMotion';
import SubpageShell from '@/components/feature/SubpageShell';
import { BookingCalendar } from '@/components/feature/BookingCalendar';
import { BOOKING_APPOINTMENTS_API, BOOKING_SLOT_MINUTES, BOOKING_TIMESLOTS_API, bookingFields, bookingTexts } from '@/components/feature/bookingConfig';
import { DIRECTIONS_URL, hours, studioImage, visitImage } from '@/mocks/home';

export default function VisitPage() {
  const photosRef = useRef<HTMLElement>(null);
  const infoRef = useRef<HTMLElement>(null);
  useMotion(photosRef);
  useMotion(infoRef);
  return (
    <SubpageShell title="Visit the Studio" description="Plan your visit and find the right appointment for you.">
      <section ref={photosRef} className="bg-background-50 pb-16 md:pb-24">
        <div data-group className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 w-full aspect-[4/3] overflow-hidden bg-background-100">
            <img data-m="focus" src={visitImage} alt="NOVA Hair Studio reception" title="NOVA Hair Studio reception" width={1400} height={1050} className="w-full h-full object-cover object-top" />
          </div>
          <div className="md:col-span-5 w-full aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-background-100">
            <img src={studioImage} alt="NOVA Hair Studio styling floor in daylight" title="NOVA Hair Studio styling floor" width={1600} height={1200} loading="lazy" className="w-full h-full object-cover object-center" />
          </div>
        </div>
      </section>

      <section ref={infoRef} className="bg-background-100 border-t border-background-300 py-16 md:py-24">
        <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-12">
          <div data-group className="lg:col-span-5">
            <h2 data-m="title" className="font-heading font-semibold tracking-[-0.02em] text-[34px] md:text-[48px] leading-[1.05] text-foreground-950">Location</h2>
            <dl className="mt-8 border-t border-foreground-950 text-[15px]">
              {[['Salon', 'NOVA Hair Studio'], ['Address', '[Street address], [City, Postcode]'], ['Phone', '[Phone number]'], ['Email', '[Email]']].map(([k, v]) => (
                <div key={k} data-m="row" data-at="0.2" className="grid grid-cols-[96px_1fr] gap-4 py-4 border-b border-background-300">
                  <dt className="text-foreground-600">{k}</dt><dd className="text-foreground-950">{v}</dd>
                </div>
              ))}
            </dl>
            {DIRECTIONS_URL ? (
              <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-sm border border-foreground-950 px-6 py-4 text-sm text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                Get Directions <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
              </a>
            ) : (
              <p className="mt-6 text-[13px] text-foreground-600">Directions link appears once the address is confirmed.</p>
            )}
          </div>
          <div data-group className="lg:col-span-6 lg:col-start-7">
            <h2 data-m="title" className="font-heading font-semibold tracking-[-0.02em] text-[34px] md:text-[48px] leading-[1.05] text-foreground-950">Opening Hours</h2>
            <ul className="mt-8 border-t border-foreground-950">
              {hours.map((h) => (
                <li key={h.day} data-m="row" data-at="0.2" className="flex justify-between py-4 border-b border-background-300 text-[15px]">
                  <span className="text-foreground-950">{h.day}</span>
                  <span className="text-foreground-600">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="book" className="scroll-mt-20 bg-background-50 border-t border-background-300 py-16 md:py-24">
        <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-8 items-end">
          <div className="lg:col-span-7">
            <h2 className="font-heading font-semibold tracking-[-0.02em] leading-[1.05] text-[34px] md:text-[48px] text-foreground-950">Book an Appointment</h2>
            <p className="mt-5 text-[16px] leading-relaxed text-foreground-700 max-w-lg">Pick a date and time below, then add your details. New clients start with a short consultation.</p>
          </div>
          <div className="lg:col-span-12 mt-6">
            <BookingCalendar
              timeslotsApi={BOOKING_TIMESLOTS_API}
              appointmentsApi={BOOKING_APPOINTMENTS_API}
              texts={bookingTexts}
              fields={bookingFields}
              slotMinutes={BOOKING_SLOT_MINUTES}
            />
          </div>
        </div>
      </section>
    </SubpageShell>
  );
}